export const WEB_CHART_ROOT_ENTRY_REDIRECT_KEY = "webChartRootEntryRedirectPending"

export type WebChartEntryDeviceType = "phone" | "tablet" | "desktop"

interface WebChartEntryDeviceDetectionInput {
  hasElectronApi?: boolean
  userAgent?: string
  platform?: string
  maxTouchPoints?: number
  viewportWidth?: number
}

export const getWebChartEntryDeviceType = ({
  hasElectronApi,
  userAgent,
  platform,
  maxTouchPoints,
  viewportWidth,
}: WebChartEntryDeviceDetectionInput = {}): WebChartEntryDeviceType => {
  if (typeof window === "undefined" && typeof hasElectronApi === "undefined") {
    return "desktop"
  }

  const resolvedHasElectronApi = typeof hasElectronApi === "boolean" ? hasElectronApi : Boolean(window.electronAPI)

  if (resolvedHasElectronApi) {
    return "desktop"
  }

  const resolvedUserAgent = userAgent ?? navigator.userAgent ?? ""
  const resolvedPlatform = platform ?? navigator.platform ?? ""
  const resolvedMaxTouchPoints = maxTouchPoints ?? navigator.maxTouchPoints ?? 0
  const resolvedViewportWidth = viewportWidth ?? window.innerWidth ?? 0
  const isIPhone = /iPhone|iPod/.test(resolvedUserAgent)
  const isIPad = /iPad/.test(resolvedUserAgent)
  const isIPadOS = resolvedPlatform === "MacIntel" && resolvedMaxTouchPoints > 1
  const isAndroid = /Android/.test(resolvedUserAgent)
  const isAndroidTablet = isAndroid && !/Mobile/.test(resolvedUserAgent)
  const isTouchTabletViewport = resolvedMaxTouchPoints > 1 && resolvedViewportWidth >= 768 && resolvedViewportWidth <= 1400
  const isDesktopLikeTabletBrowser = !/Win/.test(resolvedPlatform) && !isIPhone && !isIPad && !isIPadOS && !isAndroid && isTouchTabletViewport

  if (isIPhone) {
    return "phone"
  }

  if (isIPad || isIPadOS || isAndroidTablet) {
    return "tablet"
  }

  if (isDesktopLikeTabletBrowser) {
    return "tablet"
  }

  if (isAndroid) {
    return "phone"
  }

  return "desktop"
}

export const isMobileWebBrowserEntry = () => {
  const deviceType = getWebChartEntryDeviceType()
  return deviceType === "phone" || deviceType === "tablet"
}

export const isPhoneWebBrowserEntry = () => {
  return getWebChartEntryDeviceType() === "phone"
}

interface WebChartEntryNormalizationInput {
  hasElectronApi: boolean
  isCapacitorApp: boolean
}

interface BareWebChartEntryRedirectInput {
  pathname: string
  hash: string
  hasValidSession: boolean
  preferMobileEntry?: boolean
}

interface BareWebChartEntryRedirectResult {
  nextPathname: string
  nextHash: "#/login" | "#/mobile"
  shouldRedirectToDashboard: boolean
}

const resolveWebChartEntryPathname = (pathname: string) => {
  const normalizedPathname = pathname.trim() || "/"

  if (normalizedPathname === "/" || normalizedPathname.endsWith("/index.html") || normalizedPathname.endsWith("/")) {
    return normalizedPathname
  }

  const lastSlashIndex = normalizedPathname.lastIndexOf("/")

  if (lastSlashIndex <= 0) {
    return "/"
  }

  return normalizedPathname.slice(0, lastSlashIndex + 1)
}

export const shouldNormalizeWebChartEntry = ({ hasElectronApi, isCapacitorApp }: WebChartEntryNormalizationInput) => {
  return !hasElectronApi && !isCapacitorApp
}

export const resolveBareWebChartEntryRedirect = ({
  pathname,
  hash,
  hasValidSession,
  preferMobileEntry = false,
}: BareWebChartEntryRedirectInput): BareWebChartEntryRedirectResult | null => {
  const normalizedHash = hash.trim()
  const isBareHash = normalizedHash.length === 0 || normalizedHash === "#"

  if (!isBareHash) {
    return null
  }

  return {
    nextPathname: resolveWebChartEntryPathname(pathname),
    nextHash: preferMobileEntry ? "#/mobile" : "#/login",
    shouldRedirectToDashboard: preferMobileEntry ? false : hasValidSession,
  }
}

